import React, { useEffect, useContext, useCallback } from 'react';
import { Button, Loading } from '@byte-design/ui';
import { observer, useLocalStore } from 'mobx-react';
import Styles from './index.module.scss';
import { globalInfoStoreContext } from '@/models/global';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Team } from '@/assets/noTeam.svg';
import { ReactComponent as AuthTeam } from '@/assets/authTeam.svg';
import Footer from '@/component/Footer';
import { useHistory } from 'react-router-dom';

interface Props{
  type: string;
  children: JSX.Element | JSX.Element[];
}

const Main: React.FC<Props> = ({ children, type }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const globalStore = useContext(globalInfoStoreContext);
  const {
    teamList, openSceneList,
    activeOpensceneId, activeTeamId,
    activeTeamInfo, activeOpensceneInfo,
    hasSceneList, hasTeamList, user,
  } = globalStore;

  const state = useLocalStore(() => ({
    status: 0, // 0 为加载中  1 为展示children  2 为展示无权限  3 当前团队、场景不存在 4 当前没有加入任何团队和场景
  }));

  useEffect(() => {
    if (type === 'business') {
      if (user.isSuperAdmin) {
        state.status = 1;
        return;
      }
      state.status = 4;
      return;
    }
    if (type === 'team') {
      if (activeTeamId && !activeTeamInfo.name) {
        state.status = 3;
        return;
      }
      if (activeTeamId && activeTeamInfo.role === 'enterprise_anonymous') {
        state.status = 2;
        return;
      }
      if (teamList.length && activeTeamInfo.role !== 'enterprise_anonymous') {
        state.status = 1;
        return;
      }
      if (!hasTeamList) {
        state.status = 4;
      }
    }
    if (type === 'scene') {
      if (activeOpensceneId && !activeOpensceneInfo.name) {
        state.status = 3;
        return;
      }
      if (activeOpensceneId && activeOpensceneInfo.role === 'enterprise_anonymous') {
        state.status = 2;
        return;
      }
      if (openSceneList.length && activeOpensceneInfo.role !== 'enterprise_anonymous') {
        state.status = 1;
      }
      if (!hasSceneList) {
        state.status = 4;
      }
    }
  }, [teamList, openSceneList, hasSceneList, hasTeamList, activeTeamInfo, activeOpensceneInfo]);

  const joinTeam = useCallback(() => {
    history.push('/team/join');
  }, []);

  const joinScene = useCallback(() => {
    window.location.href = `${window.location.origin}/gateway/openscene/join`;
  }, []);

  return (
    <>
      { state.status === 1 && children }
      { [0, 2, 3, 4].includes(state.status) && (
        <div className={Styles.main}>
          <div className={Styles.context}>
            {/* 加载中 */}
            { state.status === 0 && <Loading show /> }
            {/* 企业管理员 */}
            {state.status === 4 && type === 'business' && (
              <div className={Styles.tip}>
                <AuthTeam />
                <div
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: t('no_auth_on_business') }}
                />
              </div>
            )}
            {/* 不存在 */}
            { state.status === 3 && (
              <div className={Styles.exit}>
                {type === 'scene' ? t('scene_unexit_tip') : t('team_unexit_tip')}
              </div>
            ) }
            {/* 没有加入任何团队 和 场景 */}
            {state.status === 4 && type !== 'business' && (
              <div className={Styles.tip}>
                <AuthTeam />
                <div>{type === 'team' ? t('no_join_any_team') : t('no_join_any_scene') }</div>
                <Button
                  type="primary"
                  onClick={type === 'team' ? joinTeam : joinScene}
                >
                  {type === 'team' ? t('go_join_team') : t('go_join_scene')}
                </Button>
              </div>
            )}
            {type === 'team' && state.status === 2 && (
            <div className={Styles.tip}>
              {activeTeamInfo.role !== 'enterprise_anonymous'
                ? (
                  <>
                    <Team />
                    <div>{t('no_auth_on_team')}</div>
                    <Button type="primary" onClick={joinTeam}>{t('go_join_team')}</Button>
                  </>
                )
                : (
                  <>
                    <AuthTeam />
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: t('no_auth_on_current_team', { name: activeTeamInfo.name }) }}
                    />
                    <Button type="primary" onClick={joinTeam}>{t('go_join_team')}</Button>
                  </>
                )}
            </div>
            )}
            {type === 'scene' && state.status === 2 && (
            <div className={Styles.tip}>
              {activeOpensceneInfo.role !== 'enterprise_anonymous'
                ? (
                  <>
                    <Team />
                    <div>{t('no_auth_on_scene')}</div>
                    <Button type="primary" onClick={joinScene}>{t('go_join_scene')}</Button>
                  </>
                ) : (
                  <>
                    <AuthTeam />
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: t('no_auth_on_current_scene', { name: activeOpensceneInfo.name }) }}
                    />
                    <Button type="primary" onClick={joinScene}>{t('go_join_scene')}</Button>
                  </>
                )}
            </div>
            )}
          </div>
          <Footer />
        </div>
      )}
    </>
  );
};

export default React.memo(observer(Main));
